import { ReportMetadata, VorankuendigungData } from '../types';

/**
 * Punkt 1 und 2 der Vorankündigung (Ort der Baustelle, Bauherr) im Abgleich
 * mit den Projektdaten.
 *
 * Regel (Plan Planungsphase, Stufe 0.6): Der Abgleich FÜLLT nur leere Felder.
 * Steht schon etwas drin und weicht es von den Projektdaten ab, überschreibt
 * er nichts — die Ansicht zeigt dann einen Hinweis mit dem Projektwert und
 * lässt den Anwender je Feld entscheiden. Bis dahin ersetzte der Effekt in
 * `VorankuendigungView` beide Punkte bei jeder Abweichung still, auch
 * Handkorrekturen (etwa eine Baustellenbezeichnung, die vom Projektnamen
 * abweicht).
 *
 * Reine Funktionen, ohne DOM — die Ansicht ruft sie im Abgleich-Effekt
 * (Füllen) und beim Rendern (Hinweis) auf. Der Hinweis entsteht nur aus dem
 * Vergleich und schreibt nichts in den Datensatz, löst also auch kein
 * Autospeichern aus.
 */

export type AbgleichFeld = 'siteAddress' | 'clientName' | 'clientAddress';

export const ABGLEICH_FELDER: readonly AbgleichFeld[] = ['siteAddress', 'clientName', 'clientAddress'];

/** Beschriftung für den Hinweis im Editor (nicht für das Behördenblatt). */
export const ABGLEICH_FELD_LABEL: Record<AbgleichFeld, string> = {
    siteAddress: 'Punkt 1 – Bezeichnung und Ort der Baustelle',
    clientName: 'Punkt 2 – Bauherr, Name / Firma',
    clientAddress: 'Punkt 2 – Bauherr, Anschrift',
};

export interface Abweichung {
    feld: AbgleichFeld;
    /** Wert, der in der Vorankündigung steht. */
    aktuell: string;
    /** Wert, der aus den Projektdaten käme. */
    projekt: string;
}

export interface PunktEinsZweiAbgleich {
    /** Leere Felder und der Projektwert, mit dem sie gefüllt werden. */
    fuellen: Partial<Record<AbgleichFeld, string>>;
    /** Gefüllte Felder, die von den Projektdaten abweichen — nur Hinweis, nie überschreiben. */
    abweichungen: Abweichung[];
}

/**
 * Projektadresse bereinigen. Aus dem Format „Straße, Nr. PLZ Ort" (Komma an
 * der falschen Stelle) wird „Straße Nr., PLZ Ort". Die Regel stammt aus dem
 * bisherigen Abgleich-Effekt und bleibt unverändert: Komma, Leerzeichen,
 * Hausnummer (mit optionalem Buchstaben), Leerzeichen, 4–5-stellige PLZ.
 */
export const bereinigeProjektAdresse = (adresse: string | undefined | null): string =>
    (adresse || '').replace(/, (\d+[a-zA-Z]?)\s+(\d{4,5})/, ' $1, $2');

/** Punkt 1 aus den Projektdaten: „{Projektname}, {bereinigte Adresse}". */
export const ortAusProjekt = (meta: Pick<ReportMetadata, 'projectName' | 'projectAddress'> | undefined | null): string =>
    [meta?.projectName, bereinigeProjektAdresse(meta?.projectAddress)].filter(Boolean).join(', ');

type AbgleichQuelle = Pick<ReportMetadata, 'projectName' | 'projectAddress' | 'clientName' | 'clientAddress'>;

/** Die drei Zielwerte aus den Projektdaten; leer, wenn das Projekt nichts hergibt. */
export const projektwertePunktEinsZwei = (meta: AbgleichQuelle | undefined | null): Record<AbgleichFeld, string> => ({
    siteAddress: ortAusProjekt(meta),
    clientName: meta?.clientName || '',
    clientAddress: meta?.clientAddress || '',
});

/**
 * Vergleichsform: Zeilenumbrüche und Mehrfach-Leerzeichen zählen nicht als
 * Abweichung. Punkt 1 ist im Editor ein mehrzeiliges Feld — wer die Anschrift
 * nur umbricht, hat sie nicht geändert.
 */
const vergleichsform = (wert: string | undefined | null): string => (wert || '').replace(/\s+/g, ' ').trim();

/**
 * Entscheidet je Feld, ob gefüllt wird oder ein Hinweis entsteht.
 *
 *   - Projekt hat keinen Wert → nichts (weder füllen noch Hinweis).
 *   - Feld leer (auch nur Leerraum) → mit dem Projektwert füllen.
 *   - Feld gefüllt und gleich → nichts.
 *   - Feld gefüllt und abweichend → Abweichung melden, nicht überschreiben.
 */
export const gleichePunktEinsZweiAb = (
    data: Partial<Pick<VorankuendigungData, AbgleichFeld>>,
    meta: AbgleichQuelle | undefined | null,
): PunktEinsZweiAbgleich => {
    const ziel = projektwertePunktEinsZwei(meta);
    const out: PunktEinsZweiAbgleich = { fuellen: {}, abweichungen: [] };

    ABGLEICH_FELDER.forEach(feld => {
        const projekt = ziel[feld];
        if (!vergleichsform(projekt)) return;

        const aktuell = data[feld] || '';
        if (!vergleichsform(aktuell)) {
            out.fuellen[feld] = projekt;
            return;
        }
        if (vergleichsform(aktuell) !== vergleichsform(projekt)) {
            out.abweichungen.push({ feld, aktuell, projekt });
        }
    });

    return out;
};
